import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowRight, User } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const REASONS = ['sale', 'donation', 'inheritance', 'other'];
const formatDateDMY = (d) => {
  if (!d) return '—';
  const p = String(d).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

/**
 * Phase 290 — Change Owner dialog.
 *
 * Replaces the previous "overwrite owner_name in the edit form" pattern with a
 * proper transition flow:
 *   1. The PREVIOUS active owner record gets `status='previous'` and the
 *      End Date specified by the admin (defaults to today).
 *   2. A NEW `apartment_owner_history` record is created with `status='active'`
 *      and the given Start Date.
 *   3. The apartment's owner_name + phone + email snapshots are updated so
 *      future invoices use the new owner. ALL historical invoices, payments,
 *      statements, repairs and notes stay linked to the previous
 *      owner_history_id (no rewrite).
 *
 * Renders as a controlled <Dialog>; parent passes `apartment` (full doc),
 * `open` flag and `onClose` / `onChanged` callbacks.
 */
export default function ChangeOwnerDialog({ apartment, open, onClose, onChanged }) {
  const { t } = useLanguage();
  const today = new Date().toISOString().split('T')[0];

  const [currentActive, setCurrentActive] = useState(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    ownership_percentage: 100,
    start_date: today,
    previous_end_date: today,
    reason: 'sale',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !apartment?.apartment_id) return;
    // Fetch the active owner record so we can show its end_date input default
    (async () => {
      try {
        const res = await axios.get(`${API}/apartments/${apartment.apartment_id}/owner-history`);
        const active = (res.data || []).find((r) => r.status === 'active');
        setCurrentActive(active || null);
      } catch {
        setCurrentActive(null);
      }
    })();
    setForm({
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      ownership_percentage: 100,
      start_date: today,
      previous_end_date: today,
      reason: 'sale',
      notes: '',
    });
  }, [open, apartment?.apartment_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() && !form.last_name.trim()) {
      toast.error(t('firstName') + ' / ' + t('lastName'));
      return;
    }
    setSubmitting(true);
    try {
      // Step 1: If an active owner exists, mark it previous with the chosen end date.
      if (currentActive?.owner_history_id) {
        await axios.put(`${API}/apartment-owner-history/${currentActive.owner_history_id}`, {
          status: 'previous',
          end_date: form.previous_end_date || today,
        });
      }
      // Step 2: Create new active owner record. Backend will auto-close any
      // remaining active rows (defensive) and stamp owner_name on apartment.
      await axios.post(`${API}/apartment-owner-history`, {
        apartment_id: apartment.apartment_id,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        ownership_percentage: parseFloat(form.ownership_percentage) || 100,
        start_date: form.start_date || today,
        status: 'active',
        reason: form.reason,
        notes: form.notes.trim(),
      });
      toast.success(t('savedSuccess'));
      if (onChanged) onChanged();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl rounded-none" data-testid="change-owner-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-heading)' }} className="flex items-center gap-2">
            <User size={18} weight="fill" /> {t('changeOwner') || 'Change Owner'}
            {apartment?.apartment_number && (
              <span className="text-zinc-400 font-mono text-sm">#{apartment.apartment_number}</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            {t('changeOwnerHint') || 'The previous owner is preserved in Owner History with an automatic End Date. The new owner is created as Active.'}
          </DialogDescription>
        </DialogHeader>

        {/* Previous owner card */}
        {currentActive && (
          <div className="bg-zinc-50 border border-zinc-200 px-4 py-3" data-testid="current-active-owner-card">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t('currentOwner') || 'Current Active Owner'}</p>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-zinc-900 text-sm">
                  {`${currentActive.first_name || ''} ${currentActive.last_name || ''}`.trim() || '—'}
                </span>
                <span className="text-[11px] text-zinc-500 font-mono">
                  {formatDateDMY(currentActive.start_date)} → {formatDateDMY(currentActive.end_date)}
                </span>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('endDate')}</Label>
                <Input
                  type="date"
                  value={form.previous_end_date}
                  onChange={(e) => setForm({ ...form, previous_end_date: e.target.value })}
                  className="h-7 rounded-none text-xs font-mono mt-0.5 w-[150px]"
                  data-testid="previous-end-date-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Arrow */}
        <div className="flex items-center justify-center text-zinc-300">
          <ArrowRight size={20} weight="bold" />
        </div>

        {/* New owner form */}
        <form onSubmit={submit} className="space-y-4">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">{t('newActiveOwner') || 'New Active Owner'}</p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('firstName')} *</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-none mt-1" autoFocus required data-testid="change-owner-first-name" />
            </div>
            <div>
              <Label>{t('lastName')}</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-none mt-1" data-testid="change-owner-last-name" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('phone')}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none mt-1" data-testid="change-owner-phone" />
            </div>
            <div>
              <Label>{t('email')}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" data-testid="change-owner-email" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>{t('startDate')} *</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-none mt-1 font-mono" required data-testid="change-owner-start-date" />
            </div>
            <div>
              <Label>{t('ownershipPercentage')}</Label>
              <Input type="number" step="0.01" min="0" max="100" value={form.ownership_percentage} onChange={(e) => setForm({ ...form, ownership_percentage: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="change-owner-percentage" />
            </div>
            <div>
              <Label>{t('reasonForChange')}</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="change-owner-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (
                    <SelectItem key={r} value={r}>{t('reason' + r.charAt(0).toUpperCase() + r.slice(1))}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>{t('notes')} <span className="text-zinc-400 text-[10px]">({t('optional')})</span></Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none mt-1 text-sm min-h-[60px]" data-testid="change-owner-notes" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={onClose}>{t('cancel')}</Button>
            <Button type="submit" disabled={submitting} className="bg-zinc-950 rounded-none" data-testid="save-change-owner-btn">
              {submitting ? '…' : (t('saveActivateNewOwner') || 'Save & Activate New Owner')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
